import React, { useEffect, useState } from "react";
import {
  getStockBars,
  getStockMarketMovers,
  getStockMostActives,
  getStockNews,
  getStockSnapshots,
} from "../marketData/marketDataApi";
import { DEFAULT_UNIVERSE } from "./NinaLiveDayScanner";
import {
  analyzeSignatureBars,
  applySignatureProductionProtection,
  findAuthenticatedRecentCatalyst,
} from "./signatureScannerAnalysis";

const BROADER_DISCOVERY_TOP = 20;
const PREMARKET_MOST_ACTIVE_TOP = 100;
const DISCOVERY_REFRESH_MS = 5 * 60 * 1000;
const MAX_DISCOVERY_AGE_MS = 15 * 60 * 1000;
const MIN_DISCOVERY_PRICE = 1;
const MAX_UNVERIFIED_ABS_CHANGE_PERCENT = 90;
const SAFE_DISCOVERY_SYMBOL_PATTERN = /^[A-Z]{1,5}$/;

function firstFiniteNumber(...values) {
  const value = values.find(
    (candidate) =>
      candidate !== null &&
      candidate !== undefined &&
      candidate !== "" &&
      Number.isFinite(Number(candidate))
  );

  return value === undefined ? null : Number(value);
}

function buildSignatureDiscoveryUniverse(payload, now = Date.now()) {
  const source = payload?.source || {};
  const lastUpdatedMs = Date.parse(payload?.last_updated || "");
  const ageMs = now - lastUpdatedMs;

  const hasAuthenticatedSource =
    source.provider === "Alpaca" &&
    source.feed === "sip" &&
    source.source_endpoint === "stock_market_movers" &&
    payload?.market_type === "stocks";

  const isFresh =
    Number.isFinite(lastUpdatedMs) &&
    ageMs >= -60 * 1000 &&
    ageMs <= MAX_DISCOVERY_AGE_MS;

  if (!hasAuthenticatedSource || !isFresh) {
    return DEFAULT_UNIVERSE;
  }

  const movers = [
    ...(Array.isArray(payload?.gainers) ? payload.gainers : []),
    ...(Array.isArray(payload?.losers) ? payload.losers : []),
  ];

  const discoveredSymbols = movers
    .filter((mover) => {
      const symbol = String(mover?.symbol || "").trim().toUpperCase();
      const price = firstFiniteNumber(mover?.price);
      const percentageChange = firstFiniteNumber(mover?.percent_change);

      return (
        SAFE_DISCOVERY_SYMBOL_PATTERN.test(symbol) &&
        price !== null &&
        price >= MIN_DISCOVERY_PRICE &&
        percentageChange !== null
      );
    })
    .map((mover) => String(mover.symbol).trim().toUpperCase());

  if (discoveredSymbols.length === 0) {
    return DEFAULT_UNIVERSE;
  }

  return [...new Set([...discoveredSymbols, ...DEFAULT_UNIVERSE])];
}


function buildMoverQuoteMap(payload, now = Date.now()) {
  const source = payload?.source || {};
  const lastUpdatedMs = Date.parse(payload?.last_updated || "");
  const ageMs = now - lastUpdatedMs;
  const hasAuthenticatedSource =
    source.provider === "Alpaca" &&
    source.feed === "sip" &&
    source.source_endpoint === "stock_market_movers" &&
    payload?.market_type === "stocks";
  const isFresh =
    Number.isFinite(lastUpdatedMs) &&
    ageMs >= -60 * 1000 &&
    ageMs <= MAX_DISCOVERY_AGE_MS;

  if (!hasAuthenticatedSource || !isFresh) {
    return {};
  }

  return [
    ...(Array.isArray(payload?.gainers) ? payload.gainers : []),
    ...(Array.isArray(payload?.losers) ? payload.losers : []),
  ].reduce((result, mover) => {
    const symbol = String(mover?.symbol || "").trim().toUpperCase();
    const price = firstFiniteNumber(mover?.price);
    const percentageChange = firstFiniteNumber(mover?.percent_change);

    if (
      SAFE_DISCOVERY_SYMBOL_PATTERN.test(symbol) &&
      price !== null &&
      price >= MIN_DISCOVERY_PRICE
    ) {
      result[symbol] = {
        price,
        percentageChange:
          percentageChange !== null &&
          Math.abs(percentageChange) <= MAX_UNVERIFIED_ABS_CHANGE_PERCENT
            ? percentageChange
            : null,
      };
    }

    return result;
  }, {});
}

function buildMostActiveDiscoveryUniverse(payload) {
  const source = payload?.source || {};
  const hasAuthenticatedSource =
    source.provider === "Alpaca" &&
    source.source_endpoint === "stock_most_actives";

  if (!hasAuthenticatedSource) {
    return DEFAULT_UNIVERSE;
  }

  const discoveredSymbols = (
    Array.isArray(payload?.most_actives) ? payload.most_actives : []
  )
    .map((item) => String(item?.symbol || "").trim().toUpperCase())
    .filter((symbol) => SAFE_DISCOVERY_SYMBOL_PATTERN.test(symbol));

  if (discoveredSymbols.length === 0) {
    return DEFAULT_UNIVERSE;
  }

  return [...new Set([...discoveredSymbols, ...DEFAULT_UNIVERSE])];
}

function normalizeSignatureSnapshot(
  symbol,
  snapshot,
  moverQuote = null
) {
  const latestTrade = snapshot?.latest_trade || {};
  const minuteBar = snapshot?.minute_bar || {};
  const dailyBar = snapshot?.daily_bar || {};
  const previousDailyBar = snapshot?.previous_daily_bar || {};

  const snapshotPrice = firstFiniteNumber(
    latestTrade.p,
    minuteBar.c,
    dailyBar.c
  );
  const moverPrice = firstFiniteNumber(moverQuote?.price);
  const price = moverPrice ?? snapshotPrice;
  const previousClose = firstFiniteNumber(previousDailyBar.c);

  if (price === null || price < MIN_DISCOVERY_PRICE) return null;

  const calculatedPercentageChange =
    previousClose !== null && previousClose > 0
      ? ((price - previousClose) / previousClose) * 100
      : null;
  const verifiedMoverChange = firstFiniteNumber(
    moverQuote?.percentageChange
  );
  const percentageChange =
    verifiedMoverChange !== null
      ? verifiedMoverChange
      : calculatedPercentageChange !== null &&
          Math.abs(calculatedPercentageChange) <=
            MAX_UNVERIFIED_ABS_CHANGE_PERCENT
        ? calculatedPercentageChange
        : null;

  return {
    symbol,
    price,
    previousClose,
    percentageChange,
    bid: firstFiniteNumber(snapshot?.latest_quote?.bp),
    ask: firstFiniteNumber(snapshot?.latest_quote?.ap),
    quoteUpdatedAt: snapshot?.latest_quote?.t || null,
    volume: firstFiniteNumber(dailyBar.v, minuteBar.v),
    updatedAt: latestTrade.t || minuteBar.t || dailyBar.t || null,
  };
}

function rankSignatureSnapshots(
  snapshots = {},
  symbols = DEFAULT_UNIVERSE,
  moverQuoteMap = {}
) {
  return symbols.map((symbol) =>
    normalizeSignatureSnapshot(
      symbol,
      snapshots[symbol],
      moverQuoteMap[symbol]
    )
  )
    .filter(Boolean)
    .sort((left, right) => {
      const leftMove =
        left.percentageChange === null
          ? -1
          : Math.abs(left.percentageChange);
      const rightMove =
        right.percentageChange === null
          ? -1
          : Math.abs(right.percentageChange);

      if (rightMove !== leftMove) return rightMove - leftMove;

      return (right.volume ?? -1) - (left.volume ?? -1);
    })
    .slice(0, 3);
}

export default function NinaDailyMarketScanner({
  onOpenTradeReview,
  onOpportunitiesChange,
}) {
  const [opportunities, setOpportunities] = useState([]);
  const [scanStatus, setScanStatus] = useState("loading");
  const [scanError, setScanError] = useState("");
  const [refreshRequestId, setRefreshRequestId] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    async function loadAuthenticatedSnapshots() {
      setScanStatus("loading");
      setScanError("");

      try {
        const [moversPayload, mostActivesPayload] = await Promise.all([
          getStockMarketMovers(
            BROADER_DISCOVERY_TOP,
            { signal: controller.signal }
          ).catch((error) => {
            if (error?.name === "AbortError") throw error;
            return null;
          }),
          getStockMostActives(
            PREMARKET_MOST_ACTIVE_TOP,
            { signal: controller.signal }
          ).catch((error) => {
            if (error?.name === "AbortError") throw error;
            return null;
          }),
        ]);

        if (!active) return;

        const moverUniverse =
          buildSignatureDiscoveryUniverse(moversPayload);
        const mostActiveUniverse =
          buildMostActiveDiscoveryUniverse(mostActivesPayload);
        const discoveryUniverse = [
          ...new Set([
            ...moverUniverse,
            ...mostActiveUniverse,
            ...DEFAULT_UNIVERSE,
          ]),
        ];

        let payload;
        try {
          payload = await getStockSnapshots(
            discoveryUniverse,
            "sip",
            { signal: controller.signal, cacheMode: "reload" }
          );
        } catch (error) {
          if (error?.name === "AbortError") throw error;
          payload = await getStockSnapshots(
            discoveryUniverse,
            undefined,
            { signal: controller.signal, cacheMode: "reload" }
          );
        }

        if (!active) return;

        const moverQuoteMap = buildMoverQuoteMap(moversPayload);
        const scannerFeed = String(payload?.source?.feed || "").trim().toLowerCase();
        const ranked = rankSignatureSnapshots(
          payload?.snapshots || {},
          discoveryUniverse,
          moverQuoteMap
        ).map((opportunity) => ({
          ...opportunity,
          scannerFeed,
          priceSource:
            moverQuoteMap[opportunity.symbol]?.price != null
              ? "SIP mover"
              : scannerFeed.toUpperCase(),
        }));

        const analyzed = await Promise.all(
          ranked.map(async (opportunity) => {
            try {
              const [barsPayload, newsPayload] = await Promise.all([
                getStockBars(
                  opportunity.symbol,
                  "5m",
                  opportunity.scannerFeed || undefined,
                  160,
                  { signal: controller.signal, cacheMode: "reload" }
                ),
                getStockNews(opportunity.symbol, 6, {
                  signal: controller.signal,
                }).catch(() => null),
              ]);

              const catalyst = findAuthenticatedRecentCatalyst(
                newsPayload,
                opportunity.symbol
              );

              const analysis = applySignatureProductionProtection(
                analyzeSignatureBars(barsPayload),
                {
                  ...opportunity,
                  catalyst,
                }
              );

              return {
                ...opportunity,
                catalyst,
                analysis,
              };
            } catch (error) {
              if (error?.name === "AbortError") throw error;

              return {
                ...opportunity,
                catalyst: null,
                analysis: {
                  status: "YELLOW",
                  reason:
                    "Authenticated confirmation data is unavailable",
                  ema20: null,
                  macd: null,
                  signal: null,
                  relativeVolume: null,
                  updatedAt: null,
                  source: null,
                },
              };
            }
          })
        );

        if (!active) return;

        setOpportunities(analyzed);
        setScanStatus(
          analyzed.length > 0 ? "ready" : "empty"
        );
      } catch (error) {
        if (!active || error?.name === "AbortError") return;

        setOpportunities([]);
        setScanStatus("error");
        setScanError(
          error?.message ||
            "Authenticated market data is unavailable."
        );
      }
    }

    loadAuthenticatedSnapshots();

    const discoveryScan = window.setInterval(
      loadAuthenticatedSnapshots,
      DISCOVERY_REFRESH_MS
    );

    return () => {
      window.clearInterval(discoveryScan);
      active = false;
      controller.abort();
    };
  }, [refreshRequestId]);

    useEffect(() => {
    if (typeof onOpportunitiesChange === "function") {
      onOpportunitiesChange(opportunities);
    }
  }, [onOpportunitiesChange, opportunities]);


  const opportunitySlots = Array.from(
    { length: 3 },
    (_, index) => opportunities[index] || null
  );

  const confirmedOpportunities = opportunities.filter(
    (opportunity) => opportunity.analysis?.status === "GREEN"
  );

  const primaryConfirmedOpportunity =
    confirmedOpportunities[0] || null;

  const statusSummary = opportunities
    .map(
      (opportunity) =>
        `${opportunity.symbol} ${
          opportunity.analysis?.status || "YELLOW"
        }`
    )
    .join(" · ");

  const canReview =
    primaryConfirmedOpportunity !== null &&
    typeof onOpenTradeReview === "function";

  return (
    <section
      className="ninaDailyMarketScanner"
      aria-labelledby="nina-daily-market-scanner-title"
    >
      <header>
        <span>PRODUCTION SIGNATURE SCANNER</span>

        <div>
          <h2 id="nina-daily-market-scanner-title">
            AI Daily Market Scanner
          </h2>
          <button
            type="button"
            onClick={() =>
              setRefreshRequestId((currentValue) => currentValue + 1)
            }
            disabled={scanStatus === "loading"}
          >
            Refresh Scanner
          </button>
        </div>

        <p>
          Authenticated Alpaca broader-market movers plus most-active
          premarket discovery, with a{" "}
          {DEFAULT_UNIVERSE?.length || 32}-symbol safe fallback and three
          ranked opportunities with confirmation alerts.
        </p>
      </header>

      <div
        className="signatureScannerAlert"
        role="status"
        aria-live="polite"
      >
        <div>
          <strong>
            {primaryConfirmedOpportunity
              ? `Confirmed opportunity alert: ${confirmedOpportunities
                  .map((opportunity) => opportunity.symbol)
                  .join(", ")}`
              : "No confirmed opportunity alert yet"}
          </strong>

          <span>
            {statusSummary ||
              (scanStatus === "loading"
                ? "Scanning authenticated candidates…"
                : "No authenticated candidates are available.")}
          </span>
        </div>

        <button
          type="button"
          disabled={!canReview}
          onClick={() => {
            if (!canReview) return;

            onOpenTradeReview({
              kind: "signature",
              evidenceStatus: "authenticated",
              symbol: primaryConfirmedOpportunity.symbol,
              currentPrice: primaryConfirmedOpportunity.price,
              previousClose:
                primaryConfirmedOpportunity.previousClose,
              volume: primaryConfirmedOpportunity.volume,
              updatedAt:
                primaryConfirmedOpportunity.analysis?.updatedAt ||
                primaryConfirmedOpportunity.updatedAt,
              provider:
                primaryConfirmedOpportunity.analysis?.source
                  ?.provider || "Alpaca",
              feed:
                primaryConfirmedOpportunity.analysis?.source?.feed ||
                "",
              dataQuality:
                primaryConfirmedOpportunity.analysis?.source
                  ?.quality_level ||
                primaryConfirmedOpportunity.analysis?.source
                  ?.quality ||
                "Authenticated 5-minute confirmation",
              notice:
                primaryConfirmedOpportunity.analysis?.reason ||
                "Authenticated Signature Scanner confirmation",
            });
          }}
        >
          Review in Scanner
        </button>
      </div>

      <section
        className="signatureOpportunitySection"
        aria-labelledby="signature-opportunities-title"
      >
        <div className="signatureOpportunityHeading">
          <h3 id="signature-opportunities-title">
            Top 3 Authenticated Opportunities
          </h3>

          <span>
            {scanStatus === "loading"
              ? "Scanning authenticated market data…"
              : scanStatus === "ready"
                ? "Authenticated rankings loaded"
                : scanStatus === "error"
                  ? scanError
                  : "No authenticated rankings available"}
          </span>
        </div>

        <div className="signatureOpportunityGrid">
          {opportunitySlots.map((opportunity, index) => {
            const rank = index + 1;
            const status =
              opportunity?.analysis?.status || "YELLOW";
            const action =
              status === "GREEN"
                ? "REVIEW"
                : status === "RED"
                  ? "AVOID"
                  : "WAIT";

            return (
              <article
                className={`signatureOpportunityTicket signatureOpportunityTicket--${status.toLowerCase()}`}
                key={opportunity?.symbol || rank}
              >
                <span>#{rank} OPPORTUNITY</span>

                {opportunity ? (
                  <button
                    type="button"
                    className="signatureSymbolSelect"
                    aria-label={`Open ${opportunity.symbol} in Trading Center`}
                    onClick={() => {
                      if (typeof onOpenTradeReview !== "function") return;

                      onOpenTradeReview({
                        kind: "signature",
                        evidenceStatus: "authenticated",
                        symbol: opportunity.symbol,
                        currentPrice: opportunity.price,
                        previousClose: opportunity.previousClose,
                        bid: opportunity.bid,
                        ask: opportunity.ask,
                        volume: opportunity.volume,
                        updatedAt: opportunity.updatedAt,
                        provider: "Alpaca",
                        feed: opportunity.scannerFeed || "sip",
                        dataQuality: "Authenticated AI Daily Market Scanner opportunity",
                        notice:
                          opportunity.analysis?.reason ||
                          "Opened from AI Daily Market Scanner.",
                      });
                    }}
                  >
                    {opportunity.symbol}
                  </button>
                ) : (
                  <strong>SYMBOL PENDING</strong>
                )}

                <p>
                  {opportunity
                    ? `$${opportunity.price.toFixed(2)} · ${
                        opportunity.percentageChange === null
                          ? "Daily change unavailable"
                          : `${
                              opportunity.percentageChange >= 0
                                ? "+"
                                : ""
                            }${opportunity.percentageChange.toFixed(
                              2
                            )}% today`
                      }`
                    : "Waiting for authenticated market data and confirmation calculations."}
                </p>

                {opportunity?.scannerFeed ? (
                  <span>
                    Price source: {opportunity.priceSource || opportunity.scannerFeed.toUpperCase()}
                  </span>
                ) : null}

                <small>
                  {opportunity
                    ? `AI STATUS: ${status} · ACTION: ${action} — ${
                        opportunity.analysis?.reason ||
                        "Confirmation pending"
                      }`
                    : "AI STATUS: YELLOW · ACTION: WAIT"}
                </small>
              </article>
            );
          })}
        </div>
      </section>

            <div role="note">
        Authenticated stock snapshots, 5-minute confirmation data,
        symbol-specific news, and indicative option-chain data are connected.
       Options remain PRACTICE because executable OPRA data and brokerage
execution are unavailable.
      </div>
    </section>
  );
}